import type { AssistantProcess, Document } from "@models";
import { getLatestDocumentVersion } from "@utils";
import type { TemplateConfig } from "src/types";
import type { EditDataKeySource } from "./editDataKeySource";

export interface CurrentValueResolution {
  value: string;
  warning: string | null;
}

const getDocumentCurrentValue = ({
  dataKeyId,
  documents,
  documentId,
  versionId,
}: {
  dataKeyId: string;
  documents: Document[];
  documentId: string;
  versionId: string;
}): CurrentValueResolution => {
  const document = documents.find((candidate) => candidate.id === documentId);
  if (!document) {
    return {
      value: "",
      warning:
        "Previous value unavailable because the selected record is not loaded.",
    };
  }

  const version = document.versions.find(
    (candidate) => candidate.id === versionId
  );
  if (!version) {
    return {
      value: "",
      warning:
        "Previous value unavailable because the selected record version is not loaded.",
    };
  }

  return {
    value:
      version.answers.find((answer) => answer.element === dataKeyId)?.answer ??
      "",
    warning: null,
  };
};

const getAssistantProcessCurrentValue = ({
  dataKeyId,
  assistantProcesses,
  assistantProcessId,
}: {
  dataKeyId: string;
  assistantProcesses: AssistantProcess[] | null | undefined;
  assistantProcessId: string;
}): CurrentValueResolution => {
  const process = assistantProcesses?.find(
    (candidate) => candidate.id === assistantProcessId
  );
  if (!process) {
    return {
      value: "",
      warning:
        "Previous value unavailable because the selected assistant process is not loaded.",
    };
  }

  const matchingChanges = process.state.changes.filter(
    (change) => change.dataKeyId === dataKeyId
  );
  if (matchingChanges.length === 0) {
    return { value: "", warning: null };
  }
  if (matchingChanges.length > 1) {
    return {
      value: "",
      warning:
        "Previous value unavailable because the selected assistant process contains multiple matching changes.",
    };
  }

  const change = matchingChanges[0];
  return {
    value: change.acceptedAnswer?.answer ?? change.previousAnswer?.answer ?? "",
    warning: null,
  };
};

const getUnscopedCurrentValue = ({
  dataKeyId,
  documents,
  templatesConfig,
}: {
  dataKeyId: string;
  documents: Document[];
  templatesConfig: TemplateConfig;
}): CurrentValueResolution => {
  const matchingTemplateKeys = new Set(
    Object.values(templatesConfig)
      .filter((template) =>
        template.questions?.some((question) => question.id === dataKeyId)
      )
      .map((template) => template.templateKey)
  );
  const matchingDocuments = documents.filter(
    (document) =>
      !!document.templateKey && matchingTemplateKeys.has(document.templateKey)
  );

  if (matchingDocuments.length > 1) {
    return {
      value: "",
      warning: "Previous value unavailable because no record was specified.",
    };
  }
  if (matchingDocuments.length === 0) {
    return { value: "", warning: null };
  }

  const version = getLatestDocumentVersion(matchingDocuments[0]);
  return {
    value:
      version?.answers.find((answer) => answer.element === dataKeyId)?.answer ??
      "",
    warning: null,
  };
};

export const getEditDataKeyCurrentValue = ({
  dataKeyId,
  documents,
  templatesConfig,
  assistantProcesses,
  source,
}: {
  dataKeyId: string;
  documents: Document[] | undefined;
  templatesConfig: TemplateConfig | undefined;
  assistantProcesses: AssistantProcess[] | null | undefined;
  source: EditDataKeySource;
}): CurrentValueResolution | null => {
  if (source.kind === "invalid") {
    return { value: "", warning: source.message };
  }
  if (source.kind === "document") {
    if (!documents) return null;
    return getDocumentCurrentValue({
      dataKeyId,
      documents,
      documentId: source.documentId,
      versionId: source.versionId,
    });
  }
  if (source.kind === "assistant-process") {
    if (!assistantProcesses) return null;
    return getAssistantProcessCurrentValue({
      dataKeyId,
      assistantProcesses,
      assistantProcessId: source.assistantProcessId,
    });
  }

  if (!documents || !templatesConfig) return null;
  return getUnscopedCurrentValue({ dataKeyId, documents, templatesConfig });
};
